import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { TranslocoService } from '@jsverse/transloco';
import moment from 'moment';
import { Moment } from 'moment';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { Flightinfo } from '../../../../../../models/flightModel';
import { OrderBundleType } from '../../../../../../models/orderBundle.models';
import { Person, PERSONS } from '../../../../../../models/persons';
import { ProductOption, PRODUCT_OPTIONS } from '../../../../../../models/productOption';
import { ShuttelbussService } from '../../../../../../services/shuttlebuss.service';
import { SnackbarService } from '../../../../../../services/snackbar.service';
import { BackOffice24TravelAgencyBottomsheetComponent } from '../back-office24-travel-agency-bottomsheet/back-office24-travel-agency-bottomsheet.component';
import { ParkingService } from '../../../../../../services/parking.service';
import { MyConfigOrganization } from '../../../../../../models/myConfig';
import { BackOffice24StatesService } from '../../../state/back-office24-states.service';
import { CartService } from '../../../../../../../app/services/cart.service';

@Component({
  selector: 'app-back-office24-travel-agency-form',
  templateUrl: './back-office24-travel-agency-form.component.html',
  styleUrl: './back-office24-travel-agency-form.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class BackOffice24TravelAgencyFormComponent implements OnInit, OnDestroy {
  public datepipe: DatePipe;
  persons: Person[] = PERSONS;
  productOption: ProductOption[] = PRODUCT_OPTIONS;
  bookingForm: UntypedFormGroup;
  isLoading = false;
  isLoadingArrival = false;
  isLoadingDeparture = false;
  minDepartureDate: Date = new Date();
  minArrivalDate: Date = new Date();
  arrivalFlights: Flightinfo[] = [];
  departureFlights: Flightinfo[] = [];
  currentUtcTime: string = moment.utc().format();
  selectedArrivalTime: Moment;
  arrivalTerminal: string;
  departureTerminal: string;
  bundle: OrderBundleType;
  travelAgencyBundle: string;
  productCode: string;
  arriveAtParking60Min: string;
  arriveAtParking75Min: string;
  arriveAtParking90Min: string;
  arriveAtParking105Min: string;
  arriveAtParking120Min: string;
  arriveAtParking135Min: string;
  arriveAtParking150Min: string;
  arriveAtParking165Min: string;
  arriveAtParking180Min: string;
  arriveAtParking195Min: string;
  arriveAtParking210Min: string;
  arriveAtParking225Min: string;
  arriveAtParking240Min: string;
  arriveAtParking255Min: string;
  arriveAtParking270Min: string;

  componentDestroyed$: Subject<boolean> = new Subject();
  filteredOptionsFlightsDepartures: Observable<Flightinfo[]>;
  filteredOptionsFlightsArrivals: Observable<Flightinfo[]>;
  hasAvailableParking = true;
  selectedFlight: Flightinfo = null;
  departHour: number;
  departMinute: number;
  arriveHour: number;
  arriveMinute: number;
  selectedArrival: Flightinfo;
  selectedDeparture: Flightinfo;
  organizationId: string = 'A848CCD3-FD8D-4035-925E-571EBB4E93E8';
  parkingId: string = '8C98315A-B2AD-4E70-86F9-5D54992FED26';
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private shuttleBussService: ShuttelbussService,
    public snackbar: SnackbarService,
    public translate: TranslocoService,
    private bottomSheet: MatBottomSheet,
    public dialog: MatDialog,
    private parkingService: ParkingService,
    public backOffice24StatesService: BackOffice24StatesService,
    private cartService: CartService
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );
    this._initForm();
    this.formReset();
  }

  getAvailableParkingsForOrganization(parkingId: string, dateFrom: string, dateTo: string) {
    console.log(this.organization?.organizationId, dateFrom, dateTo);
    this.cartService
      .getAvailableParkings(this.organization?.organizationId, dateFrom, dateTo, 'NONE')
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (parkings) => {
          const parkingList = parkings?.parkings;
          const selectedParking = parkingList?.find(p => p.id.toLowerCase() === parkingId.toLowerCase());
          if (this.bookingForm.controls['overrideParking'].value) {
            this.hasAvailableParking = true;
          } else {
            if (selectedParking && selectedParking[0] && selectedParking[0]?.availableParkingSpots <= 1) {
              this.hasAvailableParking = false;
            } else {
              this.hasAvailableParking = true;
            }
          }
        }
      });
  }

  onDepartureNumberChange(searchValue: string): void {
    this.selectedFlight = null;
  }

  getParkingTime(event: MatSelectChange) {
    this.departHour = moment(event.value).get('hour');
    this.departMinute = moment(event.value).get('minute');
  }

  onArrivalNumberChange(searchValue: string): void {
    this.bookingForm.controls['flightArrivalId'].patchValue('');
  }

  displayArrivalFlightWrapper() {
    return (offer) => this.displayArrivalFlight(offer);
  }

  displayArrivalFlight(option?: any): string | undefined {
    if (option == '') {
      return '';
    }
    let formattedDate = (moment(option.arrivalTime.scheduled)).format('HH:mm')
    let departureAirport = '';
    if (this.translate.getActiveLang() == 'sv') {
      departureAirport = option.departureAirportSwedish;
    }
    else if (this.translate.getActiveLang() == 'en') {
      departureAirport = option.departureAirportEnglish;
    }
    this.selectedArrivalTime = moment(option.arrivalTime.scheduled);
    this.arrivalTerminal = option.locationAndStatus.terminal;
    this.arriveHour = moment(this.selectedArrivalTime).get('hour');
    this.arriveMinute = moment(this.selectedArrivalTime).get('minute');
    this.bookingForm.controls['flightArrivalId'].patchValue(option.flightId);

    return option ? option.flightId + ' ' + departureAirport + ' ' + formattedDate : null;
  }

  checkIfFlightExists() {
    this.bookingForm.controls['flightDepartureNumber'].valueChanges
      .pipe()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((result: string) => {
        this.departureFlights.forEach(flight => {
          if (flight.flightId !== result) {
            this.bookingForm.controls['parkingfrom'].disable();
            this.bookingForm.controls['parkingfrom'].updateValueAndValidity();
          }
        });
      });
  }

  private _initForm() {
    this.bookingForm = this.fb.group({
      flightDepartureDate: ['', Validators.required],
      flightDepartureNumber: ['', [Validators.pattern(/^\S*$/), Validators.required]],
      parkingfrom: ['', Validators.required],
      flightArrivalDate: ['', Validators.required],
      flightArrivalNumber: ['', [Validators.required]],
      flightArrivalId: ['', [Validators.pattern(/^\S*$/), Validators.required]],
      carid: ['XXX123', [Validators.required, Validators.minLength(6)]],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: [''],
      cellular: [''],
      persons: ['', Validators.required],
      onlyHandBaggage: [0],
      overrideParking: [false],
      comment: [''],
      travelAgency: ['', Validators.required]
    });
  }

  get bookingtForm() {
    return this.bookingForm.controls;
  }

  formReset() {
    this.shuttleBussService.onFormReset.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
      this.resetForm();
    });
  }

  private _filterAutocompleteDepartures(value: string): Flightinfo[] {
    const filterValue = value.toLowerCase();
    return this.departureFlights.filter(
      option =>
        option.flightId.toLowerCase().includes(filterValue) ||
        option.arrivalAirportSwedish.toLowerCase().includes(filterValue) ||
        option.arrivalAirportEnglish.toLowerCase().includes(filterValue) ||
        option.codeShareData
          .toString()
          .toLowerCase()
          .includes(filterValue)
    );
  }

  private _filterAutocompleteArrivals(value: string): Flightinfo[] {
    const filterValue = value.toLowerCase();
    return this.arrivalFlights.filter(
      option =>
        option.flightId.toLowerCase().includes(filterValue) ||
        option.departureAirportSwedish.toLowerCase().includes(filterValue) ||
        option.departureAirportEnglish.toLowerCase().includes(filterValue) ||
        option.codeShareData
          .toString()
          .toLowerCase()
          .includes(filterValue)
    );
  }

  onArrivalSelect(event: MatOptionSelectionChange) {
    const arrivalFlight = this.arrivalFlights.filter(
      flight => flight.flightId === event.source.value.flightId
    )[0];
    this.selectedArrival = arrivalFlight;
    this.arrivalTerminal = arrivalFlight.locationAndStatus.terminal;
    this.selectedArrivalTime = moment(arrivalFlight.arrivalTime.scheduled);
    this.getAvailableParkingsForOrganization(this.parkingId, moment(this.bookingForm.controls['flightDepartureDate'].value).set({ h: this.departHour, m: this.departMinute }).format('YYYY-MM-DDTHH:mm'), moment(this.selectedArrival.arrivalTime.scheduled).format('YYYY-MM-DDTHH:mm'));
  }

  onDepartureSelect(event: MatSelectChange) {
    this.bookingForm.controls['parkingfrom'].reset('');
    const departureFlight = this.departureFlights.filter(
      flight => flight.flightId === event.source.value
    );
    this.selectedDeparture = departureFlight[0];
    this.selectedFlight = { ...departureFlight[0] };
    this.selectedArrivalTime = moment(this.selectedFlight.departureTime.scheduled);
    this.departureTerminal = this.selectedFlight.locationAndStatus.terminal;
    this.arriveAtParking60Min = moment(this.selectedArrivalTime)
      .subtract(60, 'm')
      .format();
    this.arriveAtParking75Min = moment(this.selectedArrivalTime)
      .subtract(75, 'm')
      .format();
    this.arriveAtParking90Min = moment(this.selectedArrivalTime)
      .subtract(90, 'm')
      .format();
    this.arriveAtParking105Min = moment(this.selectedArrivalTime)
      .subtract(105, 'm')
      .format();
    this.arriveAtParking120Min = moment(this.selectedArrivalTime)
      .subtract(120, 'm')
      .format();
    this.arriveAtParking135Min = moment(this.selectedArrivalTime)
      .subtract(135, 'm')
      .format();
    this.arriveAtParking150Min = moment(this.selectedArrivalTime)
      .subtract(150, 'm')
      .format();
    this.arriveAtParking165Min = moment(this.selectedArrivalTime)
      .subtract(165, 'm')
      .format();
    this.arriveAtParking180Min = moment(this.selectedArrivalTime)
      .subtract(180, 'm')
      .format();
    this.arriveAtParking195Min = moment(this.selectedArrivalTime)
      .subtract(195, 'm')
      .format();
    this.arriveAtParking210Min = moment(this.selectedArrivalTime)
      .subtract(210, 'm')
      .format();
    this.arriveAtParking225Min = moment(this.selectedArrivalTime)
      .subtract(225, 'm')
      .format();
    this.arriveAtParking240Min = moment(this.selectedArrivalTime)
      .subtract(240, 'm')
      .format();
    this.arriveAtParking255Min = moment(this.selectedArrivalTime)
      .subtract(255, 'm')
      .format();
    this.arriveAtParking270Min = moment(this.selectedArrivalTime)
      .subtract(270, 'm')
      .format();
  }

  getTravelAgencyBundle(event: MatSelectChange) {
    if (event.value === 'PSVING') {
      this.productCode = 'PARKCHARTER';
    }
    if (event.value === 'PSTUI1VECKA') {
      this.productCode = 'PARKCHARTER1VECKA';
    }
    if (event.value === 'PSVING1VECKA') {
      this.productCode = 'PARKCHARTER1VECKA';
    }
    if (event.value === 'PSVING2VECKA') {
      this.productCode = 'PARKCHARTER2VECKA';
    }
    if (event.value === 'PSVING3VECKA') {
      this.productCode = 'PARKCHARTER3VECKA';
    }
    if (event.value === 'PSVING4VECKA') {
      this.productCode = 'PARKCHARTER4VECKA';
    }
    if (event.value === 'PSTUI') {
      this.productCode = 'PARKCHARTER';
    }
    if (event.value === 'PSTUI2VECKA') {
      this.productCode = 'PARKCHARTER2VECKA';
    }
    if (event.value === 'PSTUI3VECKA') {
      this.productCode = 'PARKCHARTER3VECKA';
    }
    if (event.value === 'PSTUI4VECKA') {
      this.productCode = 'PARKCHARTER4VECKA';
    }
    if (event.value === 'PSSCANDIC') {
      this.productCode = 'PARKCHARTER';
    }
    if (event.value === 'PSSCANDIC1VECKA') {
      this.productCode = 'PARKCHARTER1VECKA';
    }
    this.travelAgencyBundle = event.value;
  }

  checkBaggage(e: MatCheckboxChange) {
    if (e.checked) {
      this.bookingForm.controls['onlyHandBaggage'].setValue(1);
    }
    if (!e.checked) {
      this.bookingForm.controls['onlyHandBaggage'].setValue(0);
    }
  }

  checkOverrideParking(e: MatCheckboxChange) {
    this.bookingForm.controls['overrideParking'].setValue(e.checked);
    if (this.bookingForm.controls['overrideParking'].value) {
      this.hasAvailableParking = true;
    }
  }

  resetForm() {
    this.bookingForm.reset({
      carid: this.bookingForm.get('carid').value
    });
    this.bookingForm.controls['flightArrivalId'].reset('');
    this.bookingForm.controls['flightArrivalNumber'].reset('');
    this.hasAvailableParking = true;
    window.scroll(0, 0);
  }

  getFlightsByDate(date: any, type: string) {
    const pickedDate = date.value.format('YYYY-MM-DD');
    if (type === 'departure') {
      this.minArrivalDate = pickedDate;
      this.isLoadingDeparture = true;
      this.bookingForm.controls['flightDepartureNumber'].reset('');
      this.shuttleBussService
        .getFlights(pickedDate, type)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(
          flightInfo => {
            this.departureFlights = flightInfo
              .filter(
                flight =>
                  flight.departureTime.scheduled > moment().format()
              )
              .sort((a, b) =>
                a.departureTime.scheduled < b.departureTime.scheduled ? -1 : 1
              );
            this.isLoadingDeparture = false;
            this.filteredOptionsFlightsDepartures = this.bookingForm.controls[
              'flightDepartureNumber'
            ].valueChanges.pipe(
              startWith(''),
              map(value => this._filterAutocompleteDepartures(value))
            );
          },
          error => {
            this.isLoadingDeparture = false;
            console.error(error);
            this.snackbar.openSnackBar(
              `Det gick inte att hämta flightnummer, vänligen försök igen - (${error.statusText})`,
              ''
            );
          }
        );
    }
    if (type === 'arrival') {
      this.isLoadingArrival = true;
      this.bookingForm.controls['flightArrivalId'].reset('');
      this.bookingForm.controls['flightArrivalNumber'].reset('');
      this.shuttleBussService
        .getFlights(pickedDate, type)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(
          flightInfo => {
            this.arrivalFlights = flightInfo
              .filter(
                flight =>
                  flight.arrivalTime.scheduled > moment().format()
              )
              .sort((a, b) => (a.arrivalTime.scheduled < b.arrivalTime.scheduled ? -1 : 1));
            this.isLoadingArrival = false;
            this.filteredOptionsFlightsArrivals = this.bookingForm.controls[
              'flightArrivalNumber'
            ].valueChanges.pipe(
              startWith(''),
              map(value => this._filterAutocompleteArrivals(value))
            );
          },
          error => {
            this.isLoadingArrival = false;
            console.error(error);
            this.snackbar.openSnackBar(
              `Det gick inte att hämta flightnummer, vänligen försök igen - (${error.statusText})`,
              ''
            );
          }
        );
    }
  }

  openBottomSheet() {

    let baggageVar = 1;
    if (this.bookingForm.controls['onlyHandBaggage'].value === 1) {
      baggageVar = 0;
    }
    const customerData: OrderBundleType = {
      totalPrice: 0,
      totalDiscountPrice: 0,
      discountAmount: 0,
      discountPercent: 0,
      order: {
        organizationId: this.organizationId,
        bundleCode: this.bookingForm.controls['travelAgency'].value,
        productCode: null,
        comment: ''
      },
      customer: {
        firstname: this.bookingForm.controls['firstname'].value,
        lastname: this.bookingForm.controls['lastname'].value,
        email: this.bookingForm.controls['email'].value,
        cellular: this.bookingForm.controls['cellular'].value
      },
      parking: {
        parkingId: this.parkingId,
        productCode: this.productCode,
        carId: this.bookingForm.controls['carid'].value,
        parkingFrom: moment(this.bookingForm.controls['flightDepartureDate'].value).set({ h: this.departHour, m: this.departMinute }).format(),
        parkingTo: moment(this.bookingForm.controls['flightArrivalDate'].value).set({ h: this.arriveHour, m: this.arriveMinute }).format(),
        comment: this.bookingForm.controls['comment'].value,
        extendPeriod: 0
      },
      shuttles: [
        {
          stationcode: 'ARN',
          shuttletypecode: 'flygets-p1-airport-transfer',
          productcode: 'SHUTDEP',
          shuttledate: moment(this.bookingForm.controls['flightDepartureDate'].value).set({ h: this.departHour, m: this.departMinute }).format(),
          flightdate: moment(this.selectedDeparture.departureTime.scheduled).format(),
          flightdeparturedate: this.selectedDeparture.flightLegIdentifier.flightDepartureDateUtc,
          flightnr: this.bookingForm.controls['flightDepartureNumber'].value,
          flightterminal: this.departureTerminal,
          terminal: this.departureTerminal,
          passengers: this.bookingForm.controls['persons'].value,
          childseats: 0,
          baggage: baggageVar,
          comment: this.bookingForm.controls['comment'].value
        },
        {
          stationcode: 'ARN',
          shuttletypecode: 'flygets-p1-parking-transfer',
          productcode: 'SHUTARR',
          shuttledate: moment(this.bookingForm.controls['flightArrivalDate'].value).set({ h: this.arriveHour, m: this.arriveMinute }).format(),
          flightdate: moment(this.selectedArrival.arrivalTime.scheduled).format(),
          flightdeparturedate: this.selectedArrival.flightLegIdentifier.flightDepartureDateUtc,
          flightnr: this.bookingForm.controls['flightArrivalId'].value,
          flightterminal: this.arrivalTerminal,
          terminal: this.arrivalTerminal,
          passengers: this.bookingForm.controls['persons'].value,
          childseats: 0,
          baggage: baggageVar,
          comment: this.bookingForm.controls['comment'].value
        }
      ],
      internalBooking: true
    };

    this.isLoading = true;
    this.shuttleBussService
      .addOrderBundle(customerData)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(cart => {
        if (cart) {
          this.isLoading = false;
          const sheetRef = this.bottomSheet.open(BackOffice24TravelAgencyBottomsheetComponent, {
            data: {
              cart,
              customer: customerData
            },
            panelClass: 'bottom-sheet-container'
          });
          sheetRef
            .afterDismissed()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(data => {
              console.log('BottomSheetData', data);
            });
        }
      },
        (error: any) => {
          console.error(error);
          this.snackbar.openSnackBar(`Något gick fel... (${error.status})`, error.message);
          this.isLoading = false;
        });
  }

  ngOnDestroy() {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
